import { useCallback, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { Explicatie } from './Explicatie'

export type Directie = 'asc' | 'desc'
type Valoare = string | number | Date | null | undefined

/**
 * Sortarea pe coloane, aceeasi pe toate tabelele (Madalin, 12.10.2026: „sortare pe orice tabel,
 * pe toate coloanele sortabile, peste tot"). Modelul e cel din Performanta produse: primul clic
 * sorteaza in directia fireasca a coloanei, al doilea o inverseaza.
 *
 * Tine doar starea. Un tabel cu toate randurile in browser le ordoneaza cu `ordoneaza`; unul
 * paginat de server trimite `cheie` si `directie` in query — altfel ar sorta doar pagina curenta,
 * adica o ordine falsa.
 */
export function useSortare<K extends string>(
  implicit: K,
  /** Directia la primul clic. Implicit: cifrele si datele pornesc de la cea mai mare. */
  directiaFireasca: Partial<Record<K, Directie>> = {},
  directieInitiala: Directie = directiaFireasca[implicit] ?? 'desc',
) {
  const [cheie, setCheie] = useState<K>(implicit)
  const [directie, setDirectie] = useState<Directie>(directieInitiala)

  const sorteaza = useCallback(
    (k: K) => {
      if (k === cheie) setDirectie((d) => (d === 'asc' ? 'desc' : 'asc'))
      else {
        setCheie(k)
        setDirectie(directiaFireasca[k] ?? 'desc')
      }
    },
    [cheie, directiaFireasca],
  )

  /** Ordoneaza o copie. Valorile goale stau mereu la final, in ambele directii. */
  const ordoneaza = useCallback(
    <T,>(randuri: readonly T[], valoare: (rand: T, k: K) => Valoare): T[] => {
      const semn = directie === 'asc' ? 1 : -1
      return [...randuri].sort((a, b) => {
        const va = valoare(a, cheie)
        const vb = valoare(b, cheie)
        const golA = va === null || va === undefined || va === ''
        const golB = vb === null || vb === undefined || vb === ''
        if (golA || golB) return golA === golB ? 0 : golA ? 1 : -1
        if (typeof va === 'string' && typeof vb === 'string')
          return va.localeCompare(vb, 'ro', { numeric: true, sensitivity: 'base' }) * semn
        return (Number(va) - Number(vb)) * semn
      })
    },
    [cheie, directie],
  )

  return { cheie, directie, sorteaza, ordoneaza }
}

export type Sortare<K extends string> = ReturnType<typeof useSortare<K>>

/** Capul unei coloane sortabile. */
export function CapSortabil<K extends string>({
  sortare,
  coloana,
  children,
  dreapta = false,
  explicatie,
  className = '',
}: {
  sortare: Pick<Sortare<K>, 'cheie' | 'directie' | 'sorteaza'>
  coloana: K
  children: ReactNode
  dreapta?: boolean
  explicatie?: ReactNode
  className?: string
}) {
  const activ = sortare.cheie === coloana
  const Sageata = !activ ? ArrowUpDown : sortare.directie === 'asc' ? ArrowUp : ArrowDown
  return (
    <th
      className={`px-3 py-2 font-semibold ${dreapta ? 'text-right' : 'text-left'} ${className}`}
      aria-sort={activ ? (sortare.directie === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => sortare.sorteaza(coloana)}
        className={`group inline-flex select-none items-center gap-1 rounded hover:text-b4l-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-b4l-blue ${
          dreapta ? 'flex-row-reverse' : ''
        } ${activ ? 'text-b4l-blue-dark' : ''}`}
      >
        {explicatie ? (
          <Explicatie text={explicatie} focalizabil={false}>
            {children}
          </Explicatie>
        ) : (
          children
        )}
        <Sageata
          size={12}
          strokeWidth={2.25}
          aria-hidden
          className={activ ? '' : 'text-b4l-text-dim group-hover:text-b4l-text-muted'}
        />
      </button>
    </th>
  )
}
